import { FINGER_GROUP, FINGER_LABELS, HOME_KEYS, PHYSICAL_ROWS, findKeyForChar } from '../engine/layouts.js';
import type { FingerId, Layout } from '../engine/layouts.js';

const SPECIAL_LABELS: Record<string, string> = {
  Backspace: '⌫ Backspace',
  Tab: 'Tab ⇥',
  CapsLock: 'Caps',
  Enter: 'Enter ⏎',
  ShiftLeft: 'Shift ⇧',
  ShiftRight: 'Shift ⇧',
  Space: 'medzerník',
};

interface KeyboardProps {
  layout: Layout;
  /** Znak, ktorý má používateľ práve napísať (zvýrazní sa jeho klávesa). */
  nextChar?: string | null;
  /** Naposledy stlačená fyzická klávesa (z `event.code`). */
  pressedCode?: string | null;
  /** Fyzické klávesy, ktoré si používateľ ešte nekalibroval. */
  uncalibrated?: string[];
  onKeyClick?: (code: string) => void;
  /** Menšia verzia klávesnice (napr. do úvodnej stránky). */
  compact?: boolean;
}

/**
 * Vizuálna klávesnica.
 *
 * Zámerne je to HTML mriežka a nie SVG: lepšie sa škáluje, funguje s čítačkami
 * obrazovky a farby prstov sú len CSS triedy. Fyzické pozície a prsty pochádzajú
 * z `layouts.js` a sú nezávislé od jazyka klávesnice.
 */
export function Keyboard({ layout, nextChar, pressedCode, uncalibrated = [], onKeyClick, compact }: KeyboardProps) {
  const next = nextChar ? findKeyForChar(layout, nextChar) : null;
  const nextCode = next?.code ?? null;
  // Pri veľkom písmene alebo znaku so Shiftom zvýrazníme aj Shift na opačnej strane.
  const shiftCode =
    next?.shift === true && nextCode
      ? (['KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyG', 'KeyZ', 'KeyX', 'KeyC', 'KeyV', 'KeyB'].includes(nextCode)
        ? 'ShiftRight'
        : 'ShiftLeft')
      : null;

  return (
    <div
      className={`keyboard ${compact ? 'keyboard--compact' : ''}`}
      role="img"
      aria-label="Vizuálna klávesnica so zvýraznenou ďalšou klávesou"
    >
      {PHYSICAL_ROWS.map((row, rowIndex) => (
        <div className="kb-row" key={rowIndex}>
          {row.map((key) => {
            if (!key) return null;
            const pair = layout.labels[key.code];
            const group = FINGER_GROUP[key.finger as FingerId];
            const isNext = key.code === nextCode || key.code === shiftCode;
            const classes = [
              'key',
              `finger-${group}`,
              key.code === 'Space' ? 'key-space' : '',
              HOME_KEYS.includes(key.code) ? 'is-home' : '',
              isNext ? 'is-next' : '',
              key.code === pressedCode ? 'is-pressed' : '',
              uncalibrated.includes(key.code) ? 'is-uncalibrated' : '',
            ].filter(Boolean).join(' ');

            const special = SPECIAL_LABELS[key.code];
            const title = special
              ? `${special} (${FINGER_LABELS[key.finger as FingerId]})`
              : `${pair?.[0] ?? '?'} — ${FINGER_LABELS[key.finger as FingerId]}`;

            return (
              <button
                type="button"
                key={key.code}
                className={classes}
                style={{ ['--w' as string]: key.width }}
                title={title}
                aria-label={title}
                tabIndex={onKeyClick ? 0 : -1}
                onClick={onKeyClick ? () => onKeyClick(key.code) : undefined}
              >
                {special ? (
                  <span>{special}</span>
                ) : (
                  <>
                    {pair && pair[1] !== pair[0] && <span className="shift-label">{pair[1]}</span>}
                    <span>{pair?.[0] ?? '?'}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Legenda farieb prstov. */
export function FingerLegend() {
  const groups: Array<{ group: number; label: string }> = [
    { group: 1, label: 'malíčky' },
    { group: 2, label: 'prstenníky' },
    { group: 3, label: 'prostredníky' },
    { group: 4, label: 'ukazováky' },
    { group: 0, label: 'palce' },
  ];
  return (
    <div className="row small muted" style={{ marginTop: 8 }}>
      {groups.map((g) => (
        <span key={g.group} className="row" style={{ gap: 4 }}>
          <span
            style={{
              display: 'inline-block',
              width: 14,
              height: 4,
              borderRadius: 2,
              background: `var(--f${g.group})`,
            }}
          />
          {g.label}
        </span>
      ))}
    </div>
  );
}
